/** @jsxImportSource @emotion/react */
import React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';

interface ProgressBarProps {
  current: number;
  total: number | string;
  label?: string;
  unit?: string;
  showNumbers?: boolean;
  height?: string;
  isUnlimited?: boolean;
  dangerThreshold?: number; // Percentage (0-100) at which to show danger color
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  total,
  label,
  unit = '',
  showNumbers = true,
  height = '8px',
  isUnlimited = false,
  dangerThreshold = 80,
}) => {
  // Calculate percentage safely
  const calculatePercentage = () => {
    if (isUnlimited) return 50; // Show 50% for unlimited resources
    if (typeof total !== 'number' || total <= 0) return 0;
    return Math.min((current / total) * 100, 100);
  };

  const percentage = calculatePercentage();

  const getBarColor = (theme: BirdiTheme) => {
    if (percentage >= dangerThreshold) return theme.deepOcean;
    return theme.flora;
  };

  return (
    <div css={css`
      width: 100%;
      margin: 0.5em 0;
    `}>
      {label && (
        <div css={css`
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.25em;
          font-size: 0.9em;
        `}>
          <span>{label}</span>
          {showNumbers && (
            <span css={css`font-weight: 500;`}>
              {current}{unit && unit} / {isUnlimited ? '∞' : `${total}${unit && unit}`}
            </span>
          )}
        </div>
      )}
      <div css={css`
        width: 100%;
        height: ${height};
        background-color: #e0e0e0;
        border-radius: 4px;
        overflow: hidden;
      `}>
        <div css={(theme) => css`
          width: ${percentage}%;
          height: 100%;
          background-color: ${getBarColor(theme)};
          border-radius: 4px;
          transition: width 0.3s ease-in-out;
        `} />
      </div>
      {!label && showNumbers && (
        <div css={css`
          display: flex;
          justify-content: flex-end;
          margin-top: 0.25em;
          font-size: 0.85em;
        `}>
          <span css={css`font-weight: 500;`}>
            {percentage}%
          </span>
        </div>
      )}
    </div>
  );
}; 